import { getFlowSteps, StudyType } from "@/features/studies/utils/study-flow.config";

/**
 * Report routes for each study type, on top of the "reports/overview" step every flow ends on.
 * Scene and video in-depth routes need a scene/video key, so they can't be resumed from a stored route.
 */
const EXTRA_REPORT_ROUTES: Record<StudyType, string[]> = {
  asset: ["reports/emotion/overview", "reports/memory/overview", "reports/attention/overview", "reports/in-depth"],
  scene: [],
  video: [],
};

/**
 * Default route to use when a study route is invalid
 */
const DEFAULT_ROUTE = "reports/overview";

/**
 * getValidStudyRoutes function
 * Returns every route a study of the given type can be resumed on: its flow steps plus its report routes.
 *
 * @param studyType - The study type to get routes for
 * @returns The valid routes for the study type
 */
const getValidStudyRoutes = (studyType: StudyType): string[] => [
  // Flow steps already include "reports/overview" as each flow's final step
  ...getFlowSteps(studyType).map((step) => step.route),
  ...EXTRA_REPORT_ROUTES[studyType],
];

/**
 * sanitizeStudyRoute function
 * Validates a stored study route against the routes that exist for the study's type, so a study is
 * never opened on a page its type doesn't have. Legacy asset attention sub-routes (e.g.
 * attention/clarity) redirect to the attention overview; anything else falls back to the reports overview.
 *
 * Asset and scene process pages must not store "study-process" while they re-submit processing on
 * mount - unlike video, the API restarts asset/scene pipelines on every process call.
 *
 * @param route - The study route to validate
 * @param studyType - The study's type, defaulting to asset to match the flow config
 * @returns A valid study route
 */
export const sanitizeStudyRoute = (route: string, studyType: StudyType = "asset"): string => {
  // Check if the route exists for this study type
  if (getValidStudyRoutes(studyType).includes(route)) {
    return route;
  }

  // Handle legacy attention sub-routes by redirecting to attention overview - only asset studies have one
  if (studyType === "asset" && route.startsWith("reports/attention/")) {
    return "reports/attention/overview";
  }

  // For any other invalid routes, use the default
  return DEFAULT_ROUTE;
};
